import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { getParentDestinationOptions, getSubcategoryOptions } from '../utils/issueUtils'
import InventoryItemCard from '../components/InventoryItemCard'
import Select from 'react-select'
import '../styles/selectControl.css'

function IssueScreen() {
    const navigate = useNavigate()
    const location = useLocation()
    const { inventoryItemId } = useParams()
    const [searchParams] = useSearchParams()
    const [error, setError] = useState(null)
    const [parentDestinationOptions, setParentDestinationOptions] = useState([])
    const [subcategoryOptions, setSubcategoryOptions] = useState([])
    const [destinationsLoading, setDestinationsLoading] = useState(true)
    const [subcategoriesLoading, setSubcategoriesLoading] = useState(false)
    const [inventoryItem, setInventoryItem] = useState({
        stock_item: null,
        quantity: '',
        parent_destination: '',
        destination: '', // This will be the subcategory ID or parent ID if no subcategory
        notes: '',
        comments: '',
        sell_price: '',
        max_quantity: 0,
        warehouse: { _id: '', name: '' },
        location: '',
    })

    // Load parent destination options on component mount
    useEffect(() => {
        async function loadDestinations() {
            try {
                setDestinationsLoading(true)
                const options = await getParentDestinationOptions()
                setParentDestinationOptions(options)
            } catch (error) {
                console.error('Error loading destinations:', error)
                setError('Failed to load destination options')
            } finally {
                setDestinationsLoading(false)
            }
        }
        loadDestinations()
    }, [])

    // Load subcategories when parent destination changes
    useEffect(() => {
        async function loadSubcategories() {
            if (inventoryItem.parent_destination) {
                try {
                    setSubcategoriesLoading(true)
                    const options = await getSubcategoryOptions(inventoryItem.parent_destination)
                    setSubcategoryOptions(options)
                } catch (error) {
                    console.error('Error loading subcategories:', error)
                    setSubcategoryOptions([{ value: '', label: 'Error loading subcategories', type: 'error' }])
                } finally {
                    setSubcategoriesLoading(false)
                }
            } else {
                setSubcategoryOptions([])
                setInventoryItem(prev => ({ ...prev, destination: '' })) // Clear subcategory selection
            }
        }
        loadSubcategories()
    }, [inventoryItem.parent_destination])

    useEffect(() => {
        async function fetchInventoryItem() {
            try {
                const item = await inventoryItemAPI.getById(inventoryItemId)
                
                if (!item) return;

                const formattedItem = {
                    stock_item: item.stock_item,
                    warehouse_name: item.warehouse_name,
                    location: item.location_name,
                    new_location_id: '',
                    quantity: '',
                    vendor: item.vendor_name,
                    manufacturer: item.manufacturer_name,
                    cost: item.cost,
                    notes: item.notes,
                    bb_date: item.bb_date,
                    lot: item.lot,
                    comments: item.comments || '',
                    max_quantity: item.quantity || 0,
                }
                
                setInventoryItem(formattedItem)
            } catch (error) {
                setError('Failed to load inventory item')
            }
        }

        if (inventoryItemId) {
            fetchInventoryItem()
        }
    }, [inventoryItemId])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            if (!inventoryItem.parent_destination) {
                setError('Please select a destination')
                return
            }
            
            // Check if subcategory is required (when parent has subcategories)
            const hasSubcategories = subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error')
            if (hasSubcategories && !inventoryItem.destination) {
                setError('Please select a subcategory - this destination has subcategories available')
                return
            }
            
            await inventoryItemAPI.issue(
                inventoryItemId, 
                inventoryItem.quantity, 
                inventoryItem.comments,
                inventoryItem.parent_destination,
                inventoryItem.destination,
                inventoryItem.sell_price || null
            )
            
            // Get the return filters from the location state if they exist
            // Check for returnTo query parameter first, then fall back to location state filters
            const returnTo = searchParams.get('returnTo')
            if (returnTo) {
                navigate(returnTo)
            } else {
                const returnFilters = location.state?.returnFilters || ''
                
                // Add refresh parameter to trigger data refresh
                const separator = returnFilters.includes('?') ? '&' : '?'
                const refreshParam = `${separator}refresh=1`
                
                // Navigate back to inventory with the filters preserved and refresh trigger
                navigate(`/inventory${returnFilters}${refreshParam}`)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    return (
        <Container>
            <Row className="justify-content-left">
            <Col style={{ maxWidth: '730px' }}>
            <h1>Issue Inventory Item {inventoryItemId}</h1>
            {inventoryItem?.stock_item && (
                <InventoryItemCard inventoryItem={inventoryItem} />
            )}
            <div style={{ height: '20px' }}></div>
            <Form onSubmit={submitHandler}>
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="quantity">
                            <Form.Label>Quantity* (max {inventoryItem.max_quantity}):</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                max={inventoryItem.max_quantity}
                                step="1"
                                value={inventoryItem.quantity}
                                onChange={(e) => setInventoryItem({...inventoryItem, quantity: e.target.value})}
                                required
                                placeholder="Enter quantity"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="sell_price">
                            <Form.Label>Sell Price (per unit):</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={inventoryItem.sell_price}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow only up to 2 decimal places
                                    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                        setInventoryItem({...inventoryItem, sell_price: value});
                                    }
                                }}
                                placeholder="Enter sell price"
                                autoComplete="off"
                                spellCheck="false"
                            />
                            {inventoryItem.cost && inventoryItem.sell_price && (
                                <Form.Text className="text-muted">
                                    Cost: ${Number(inventoryItem.cost).toFixed(2)} | 
                                    Profit/unit: ${(Number(inventoryItem.sell_price) - Number(inventoryItem.cost)).toFixed(2)}
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="parent_destination">
                            <Form.Label>Destination*:</Form.Label>
                            <Select
                                value={parentDestinationOptions.find(option => option.value === inventoryItem.parent_destination)}
                                onChange={(selectedOption) => {
                                    setInventoryItem({
                                        ...inventoryItem, 
                                        parent_destination: selectedOption ? selectedOption.value : '',
                                        destination: '' // Clear subcategory when parent changes
                                    });
                                }}
                                options={parentDestinationOptions}
                                placeholder={destinationsLoading ? "Loading..." : "Select destination..."}
                                isLoading={destinationsLoading}
                                isDisabled={destinationsLoading}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                required
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="subcategory">
                            <Form.Label>
                                Subcategory{subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error') ? '*' : ''}:
                            </Form.Label>
                            <Select
                                value={subcategoryOptions.find(option => option.value === inventoryItem.destination)}
                                onChange={(selectedOption) => {
                                    setInventoryItem({
                                        ...inventoryItem, 
                                        destination: selectedOption ? selectedOption.value : ''
                                    });
                                }}
                                options={subcategoryOptions}
                                placeholder={!inventoryItem.parent_destination ? "Select destination first" : subcategoriesLoading ? "Loading..." : 
                                    subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error') ? 
                                    "Select subcategory..." : "No subcategories available"}
                                isLoading={subcategoriesLoading}
                                isDisabled={!inventoryItem.parent_destination || subcategoriesLoading}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isClearable={!subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error')}
                                required={subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error')}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-4" controlId="notes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        value={inventoryItem.comments}
                        onChange={(e) => setInventoryItem({...inventoryItem, comments: e.target.value})}
                        placeholder="Enter notes (max 500 characters)"
                        autoComplete="off"
                        spellCheck="true"
                    />
                    <Form.Text className="text-muted">
                        {inventoryItem.comments?.length || 0}/500 characters
                    </Form.Text>
                </Form.Group>
                {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
                )}
                <Button variant="success" type="submit" className="me-2">
                    Issue
                </Button>

                <Button 
                    variant="secondary" 
                    onClick={() => navigate(-1)}
                >
                    Cancel
                </Button>
            </Form>
            
            </Col>
            </Row>
        </Container>
    )
}

export default IssueScreen